import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Container,
    Group,
    Paper,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Loader,
    Alert
} from "@mantine/core";

type PersonDto = {
    personId: number;
    firstName: string;
    lastName: string;
};

type EventDto = {
    eventID?: number;
    eventName?: string;
};

type PersonsResponse = {
    items?: PersonDto[];
    Items?: PersonDto[];
};

type AssignedParkingDto = {
    parkingPersonId: number;
    eventId: number;
    personId: number;
    firstName?: string;
    lastName?: string;
    note?: string;
    isActive?: boolean;
    createdAt?: string;
};

export default function ParkingAdminPage() {
    const apiUrl = process.env.REACT_APP_API_URL;

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [persons, setPersons] = useState<PersonDto[]>([]);
    const [assignedPersons, setAssignedPersons] = useState<AssignedParkingDto[]>([]);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingPersons, setLoadingPersons] = useState(false);
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [addingPersonId, setAddingPersonId] = useState<number | null>(null);
    const [removingPersonId, setRemovingPersonId] = useState<number | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        loadEvents();
    }, []);

    const eventOptions = useMemo(() => {
        return Array.from(
            new Map(
                events
                    .map((e) => {
                        const id = e.eventID;
                        const label = e.eventName;

                        if (id === undefined || id === null) return null;

                        return {
                            value: String(id),
                            label: String(label ?? `Event ${id}`),
                        };
                    })
                    .filter(
                        (x): x is { value: string; label: string } => x !== null
                    )
                    .map((x) => [x.value, x])
            ).values()
        );
    }, [events]);

    async function loadEvents() {
        if (!apiUrl) {
            setError("REACT_APP_API_URL mangler.");
            return;
        }

        setLoadingEvents(true);
        setError(null);

        try {
            const res = await fetch(`${apiUrl}/api/events/light`);

            if (!res.ok) {
                throw new Error("Kunne ikke hente events.");
            }

            const data = await res.json();
            setEvents(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setEvents([]);
            setError(err?.message || "Der opstod en fejl ved hentning af events.");
        } finally {
            setLoadingEvents(false);
        }
    }

    async function loadAssignedPersons(selectedEventId?: string | null) {
        const currentEventId = selectedEventId ?? eventId;

        if (!apiUrl || !currentEventId) {
            setAssignedPersons([]);
            return;
        }

        setLoadingAssigned(true);

        try {
            const res = await fetch(`${apiUrl}/api/parking/event/${currentEventId}`);

            if (!res.ok) {
                throw new Error("Kunne ikke hente tildelte VIP parkeringer.");
            }

            const data = await res.json();
            setAssignedPersons(Array.isArray(data) ? data : []);
        } catch {
            setAssignedPersons([]);
        } finally {
            setLoadingAssigned(false);
        }
    }

    async function searchPersons() {
        if (!apiUrl) {
            setError("REACT_APP_API_URL mangler.");
            return;
        }

        if (!eventId) {
            setError("Vælg et event først.");
            return;
        }

        setLoadingPersons(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/persons?search=${encodeURIComponent(search)}&page=1&pageSize=50&eventId=${eventId}`
            );

            if (!res.ok) {
                throw new Error("Kunne ikke hente personer.");
            }

            const data: PersonsResponse | PersonDto[] = await res.json();

            if (Array.isArray(data)) {
                setPersons(data);
            } else {
                setPersons(
                    Array.isArray(data?.items)
                        ? data.items
                        : Array.isArray(data?.Items)
                            ? data.Items
                            : []
                );
            }
        } catch (err: any) {
            setPersons([]);
            setError(err?.message || "Der opstod en fejl ved søgning.");
        } finally {
            setLoadingPersons(false);
        }
    }

    async function addParking(personId: number) {
        if (!apiUrl) {
            setError("REACT_APP_API_URL mangler.");
            return;
        }

        if (!eventId) {
            setError("Vælg et event først.");
            return;
        }

        setAddingPersonId(personId);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${apiUrl}/api/parking/add`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    eventId: Number(eventId),
                    personId
                })
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Kunne ikke tilføje personen.");
            }

            const addedPerson = persons.find((p) => p.personId === personId);

            setSuccessMessage(
                addedPerson
                    ? `${addedPerson.firstName} ${addedPerson.lastName} blev tilføjet til VIP parkering.`
                    : "Personen blev tilføjet til VIP parkering."
            );

            await loadAssignedPersons(eventId);
        } catch (err: any) {
            setError(err?.message || "Der opstod en fejl ved tilføjelse.");
        } finally {
            setAddingPersonId(null);
        }
    }

    async function removeParking(personId: number) {
        if (!apiUrl || !eventId) return;

        setRemovingPersonId(personId);
        setError(null);
        setSuccessMessage(null);

        try {
            const res = await fetch(`${apiUrl}/api/parking/${eventId}/${personId}`, {
                method: "DELETE"
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Kunne ikke fjerne personen.");
            }

            setSuccessMessage("Personen blev fjernet fra VIP parkering.");
            await loadAssignedPersons(eventId);
        } catch (err: any) {
            setError(err?.message || "Der opstod en fejl ved sletning.");
        } finally {
            setRemovingPersonId(null);
        }
    }

    return (
        <Container size="md" py="md">
            <Stack gap="md">
                <Title order={2}>VIP Parkering</Title>

                {error && (
                    <Alert color="red" variant="light">
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert color="green" variant="light">
                        {successMessage}
                    </Alert>
                )}

                <Paper withBorder p="md" radius="md">
                    <Stack gap="md">
                        {loadingEvents ? (
                            <Group>
                                <Loader size="sm" color="red" />
                                <Text>Henter events...</Text>
                            </Group>
                        ) : (
                            <Select
                                label="Event"
                                placeholder="Vælg event"
                                value={eventId}
                                onChange={(value) => {
                                    setEventId(value);
                                    setPersons([]);
                                    setSuccessMessage(null);
                                    setError(null);
                                    loadAssignedPersons(value);
                                }}
                                searchable
                                clearable
                                data={eventOptions}
                            />
                        )}

                        <Group align="end">
                            <TextInput
                                label="Søg person"
                                placeholder="Skriv navn..."
                                value={search}
                                onChange={(e) => setSearch(e.currentTarget.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        searchPersons();
                                    }
                                }}
                                style={{ flex: 1 }}
                            />
                            <Button
                                color="red"
                                onClick={searchPersons}
                                loading={loadingPersons}
                                disabled={!eventId}
                            >
                                Søg
                            </Button>
                        </Group>
                    </Stack>
                </Paper>

                <Paper withBorder p="md" radius="md">
                    <Stack gap="sm">
                        <Text fw={600}>Personer</Text>

                        {loadingPersons ? (
                            <Group>
                                <Loader size="sm" color="red" />
                                <Text>Søger personer...</Text>
                            </Group>
                        ) : persons.length === 0 ? (
                            <Text c="dimmed">Ingen personer fundet.</Text>
                        ) : (
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Navn</Table.Th>
                                        <Table.Th>PersonId</Table.Th>
                                        <Table.Th></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {persons.map((p) => (
                                        <Table.Tr key={p.personId}>
                                            <Table.Td>
                                                {p.firstName} {p.lastName}
                                            </Table.Td>
                                            <Table.Td>{p.personId}</Table.Td>
                                            <Table.Td>
                                                <Button
                                                    color="red"
                                                    size="xs"
                                                    loading={addingPersonId === p.personId}
                                                    onClick={() => addParking(p.personId)}
                                                >
                                                    Tilføj
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        )}
                    </Stack>
                </Paper>

                <Paper withBorder p="md" radius="md">
                    <Stack gap="sm">
                        <Text fw={600}>Tildelt VIP parkering</Text>

                        {!eventId ? (
                            <Text c="dimmed">Vælg et event for at se tildelte personer.</Text>
                        ) : loadingAssigned ? (
                            <Group>
                                <Loader size="sm" color="red" />
                                <Text>Henter tildelte personer...</Text>
                            </Group>
                        ) : assignedPersons.length === 0 ? (
                            <Text c="dimmed">Ingen personer er tildelt VIP parkering endnu.</Text>
                        ) : (
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Navn</Table.Th>
                                        <Table.Th>PersonId</Table.Th>
                                        <Table.Th>Oprettet</Table.Th>
                                        <Table.Th></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {assignedPersons.map((p) => (
                                        <Table.Tr key={p.parkingPersonId}>
                                            <Table.Td>
                                                {(p.firstName || "")} {(p.lastName || "")}
                                            </Table.Td>
                                            <Table.Td>{p.personId}</Table.Td>
                                            <Table.Td>
                                                {p.createdAt
                                                    ? new Date(p.createdAt).toLocaleString("da-DK")
                                                    : "-"}
                                            </Table.Td>
                                            <Table.Td>
                                                <Button
                                                    color="red"
                                                    variant="light"
                                                    size="xs"
                                                    loading={removingPersonId === p.personId}
                                                    onClick={() => removeParking(p.personId)}
                                                >
                                                    Fjern
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        )}
                    </Stack>
                </Paper>
            </Stack>
        </Container>
    );
}